import { send } from '@actual-app/core/platform/client/connection';
import type {
  FieldMapping,
  ReappliedTransaction,
  ReapplyError,
} from '@actual-app/core/types/models/enablebanking';

export type { FieldMapping, ReappliedTransaction, ReapplyError };

// The loot-core proxies return the sync-server `data` field directly. On
// validation errors the server still responds 200 with an `{ error_code,
// error_type }` shape, so we sniff for that here.
type ServerError = { error_code?: string; error_type?: string; error?: string };

function isServerError(value: unknown): value is ServerError {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('error_type' in value || 'error' in value)
  );
}

export async function getFieldMappings(
  // Pass the EnableBanking account_id here | the server stores it as bank_id.
  bank_id: string,
): Promise<FieldMapping[]> {
  const data = await send('enablebanking-field-mappings-list', { bank_id });
  if (Array.isArray(data)) {
    return data;
  }
  return [];
}

export async function saveFieldMapping(mapping: {
  id?: string;
  bank_id: string;
  field_name: string;
  eta_template: string;
  enabled: boolean;
}): Promise<{ success: boolean; validation_error?: string }> {
  const data = await send('enablebanking-field-mappings-save', mapping);
  if (isServerError(data)) {
    return { success: false, validation_error: data.error_type ?? data.error };
  }
  if (data && typeof data === 'object' && 'success' in data) {
    return { success: Boolean(data.success) };
  }
  return { success: false };
}

export async function testFieldMapping(
  eta_template: string,
  sample_transaction: unknown,
): Promise<{ success: boolean; result?: string; error?: string }> {
  const data = await send('enablebanking-field-mappings-test', {
    eta_template,
    sample_transaction,
  });
  if (isServerError(data) && !('success' in data)) {
    return { success: false, error: data.error_type ?? data.error };
  }
  if (data && typeof data === 'object' && 'success' in data) {
    const result = data as {
      success: boolean;
      result?: string;
      error?: string;
    };
    return result;
  }
  return { success: false };
}

export async function deleteFieldMapping(
  id: string,
): Promise<{ success: boolean }> {
  const data = await send('enablebanking-field-mappings-delete', { id });
  if (data && typeof data === 'object' && 'success' in data) {
    return { success: Boolean(data.success) };
  }
  return { success: false };
}

export async function getPreviewTransactions(
  account_id: string,
): Promise<unknown[]> {
  const data = await send('enablebanking-field-mappings-preview', {
    account_id,
  });
  if (data && typeof data === 'object' && 'transactions' in data) {
    const { transactions } = data as { transactions: unknown[] };
    return transactions ?? [];
  }
  return [];
}

// Re-fetches transactions from the bank and re-applies the saved mappings.
// `mapping_id` defaults to the account_id on the server when omitted, matching
// the per-account storage contract.
export async function reapplyFieldMappings(
  account_id: string,
  mapping_id?: string,
  startDate?: string,
): Promise<{ transactions: ReappliedTransaction[]; errors: ReapplyError[] }> {
  const data = await send('enablebanking-field-mappings-reapply', {
    account_id,
    mapping_id,
    startDate,
  });
  if (data && typeof data === 'object' && 'transactions' in data) {
    const result = data as {
      transactions: ReappliedTransaction[];
      errors: ReapplyError[];
    };
    return {
      transactions: result.transactions ?? [],
      errors: result.errors ?? [],
    };
  }
  return { transactions: [], errors: [] };
}
